/**
 * Formato de fio do oplog: um "segmento" = um lote de mensagens serializado em JSON e CIFRADO
 * E2E (mesma cifra do backup — ADR 0028), que viaja como arquivo no Drive. Cada aparelho só
 * escreve nos PRÓPRIOS segmentos (`kontto_oplog_<deviceId>_<seq>.kseg`), append-only, então não
 * há conflito de escrita no Drive. Ver docs/features/SYNC.md § 3.
 *
 * Carrega o índice do journal Drizzle (versão de schema): um aparelho recusa segmento de schema
 * MAIS NOVO que o seu (§ 6, compatibilidade de mão única) — segue 100% local e continua enviando.
 */

import { DEFAULT_KDF, type EnvelopeVersion, decryptWithKey, encryptString, generateSalt, parseEnvelope } from '@/lib/backupCrypto';
import journal from '@/drizzle/meta/_journal.json';
import { ENVELOPE_READ_MAX } from './capabilities';
import { type Message } from './messages';

export const SEGMENT_SCHEMA_VERSION = Math.max(...journal.entries.map((e) => e.idx));

/**
 * Versão do envelope que os segmentos saem QUANDO NINGUÉM SEGURA (ADR 0177): o ciclo passa a
 * versão NEGOCIADA com os outros aparelhos do espaço (ADR 0181, capabilities.ts) — um peer em
 * app antigo lê um segmento v2 como "chave errada" → `null` → o ciclo BLOQUEIA aquele aparelho
 * sem avançar o cursor (sync.ts), e enquanto ele estiver vivo no espaço todo mundo escreve v1.
 */
export const SEGMENT_ENVELOPE_MAX: EnvelopeVersion = ENVELOPE_READ_MAX;

export interface OplogSegmentBody {
    schemaVersion: number;
    deviceId: string;
    seq: number;
    messages: Message[];
    /**
     * Presente só na ÚLTIMA parte de um CHECKPOINT (ADR 0040): cobertura `device → seq
     * consumido` no momento da compactação (o próprio aparelho aparece com o último seq normal
     * coberto). CONTRATO: a presença do campo marca "checkpoint completo até aqui" — as partes
     * anteriores sobem SEM basis, então um leitor futuro (auto-cura, fase 2) jamais pode tratar
     * um checkpoint de upload interrompido como cobertura válida. Nenhum leitor DEPENDE dele
     * hoje (diagnóstico); app antigo ignora o campo extra.
     */
    basis?: Record<string, number>;
}

/**
 * Serializa (JSON) + cifra um lote de mensagens num envelope de segmento, com a CHAVE DE SYNC
 * COMPARTILHADA (a mesma em todos os aparelhos — ver syncKey.ts). O salt embutido é irrelevante
 * aqui (decifra é por chave, não por senha), então geramos um throwaway só pra preencher o
 * envelope; o que importa é que a `key` seja idêntica entre os aparelhos.
 */
export function serializeSegment(
    deviceId: string,
    seq: number,
    messages: Message[],
    key: Uint8Array,
    basis?: Record<string, number>,
    version: EnvelopeVersion = SEGMENT_ENVELOPE_MAX,
): string {
    // JSON.stringify omite `basis: undefined` — segmento normal e checkpoint saem idênticos ao formato de sempre.
    const body: OplogSegmentBody = { schemaVersion: SEGMENT_SCHEMA_VERSION, deviceId, seq, messages, basis };
    return encryptString(JSON.stringify(body), key, generateSalt(), DEFAULT_KDF, version);
}

/**
 * Decifra + parseia um segmento. Devolve null (NÃO lança) se:
 *  - não é um envelope válido, OU a chave não bate (tag GCM inválida — segmento de outra senha
 *    ou legado cifrado com a chave errada): pular é o certo, um segmento ruim não pode derrubar
 *    o ciclo inteiro;
 *  - o schema é MAIS NOVO que o deste app (recusa de mão única — o peer precisa atualizar).
 */
export function deserializeSegment(envelope: string, key: Uint8Array): OplogSegmentBody | null {
    const parsed = parseEnvelope(envelope);
    if (!parsed) return null;
    let json: string;
    try {
        json = decryptWithKey(parsed, key);
    } catch {
        return null; // tag GCM inválida — chave errada/segmento de outra senha
    }
    const body = JSON.parse(json) as OplogSegmentBody;
    if (typeof body.schemaVersion === 'number' && body.schemaVersion > SEGMENT_SCHEMA_VERSION) return null;
    if (!Array.isArray(body.messages)) return null;
    return body;
}
